import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Megaphone, Users, RefreshCw, Send, FlaskConical, Loader2, Sparkles, CheckCircle2, Clock, CalendarClock, XCircle, Eye, BarChart3 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { BroadcastHeaderImage, BroadcastDiscountCode } from './BroadcastExtras';
import { BroadcastFeaturedProducts, BroadcastReportDialog } from './BroadcastInsights';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AUDIENCES = [
  { id: 'all', label: 'All customers', desc: 'Everyone with an account (opted-in)' },
  { id: 'wholesale', label: 'Wholesale customers', desc: 'Flagged wholesale accounts' },
  { id: 'newsletter', label: 'Newsletter subscribers', desc: 'Your newsletter list' },
  { id: 'custom', label: 'Custom emails', desc: 'Paste specific addresses' },
];

const fmtDate = (s) => { try { return new Date(s).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return '—'; } };

const AdminBroadcastTab = ({ adminFetch }) => {
  const [audience, setAudience] = React.useState('all');
  const [customEmails, setCustomEmails] = React.useState('');
  const [subject, setSubject] = React.useState('');
  const [body, setBody] = React.useState('');
  const [testEmail, setTestEmail] = React.useState('');
  const [scheduleOn, setScheduleOn] = React.useState(false);
  const [scheduledAt, setScheduledAt] = React.useState('');
  const [count, setCount] = React.useState(null);
  const [previewing, setPreviewing] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [history, setHistory] = React.useState([]);
  const [headerImage, setHeaderImage] = React.useState('');
  const [headerLink, setHeaderLink] = React.useState('');
  const [discountCode, setDiscountCode] = React.useState('');
  const [previewHtml, setPreviewHtml] = React.useState(null);
  const [uniqueCodes, setUniqueCodes] = React.useState(false);
  const [featuredIds, setFeaturedIds] = React.useState([]);
  const [reportId, setReportId] = React.useState(null);
  const pickCode = (c) => { setDiscountCode(c); if (!c) setUniqueCodes(false); };

  const loadHistory = React.useCallback(async () => {
    try {
      const r = await adminFetch(`${API_URL}/api/admin/broadcast/history`);
      const d = await r.json();
      if (r.ok) setHistory(d.broadcasts || []);
    } catch (_) {}
  }, [adminFetch]);

  React.useEffect(() => { loadHistory(); }, [loadHistory]);
  React.useEffect(() => { setCount(null); }, [audience, customEmails]);

  const payload = () => ({ audience, custom_emails: customEmails, subject, body, header_image_url: headerImage, header_link: headerLink, discount_code: discountCode, unique_codes: uniqueCodes && !!discountCode, featured_product_ids: featuredIds });

  const showPreview = async () => {
    try {
      const r = await adminFetch(`${API_URL}/api/admin/broadcast/render`, { method: 'POST', body: JSON.stringify(payload()) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      setPreviewHtml(d.html);
    } catch (e) { toast.error(e.message); }
  };

  const preview = async () => {
    setPreviewing(true);
    try {
      const r = await adminFetch(`${API_URL}/api/admin/broadcast/preview`, { method: 'POST', body: JSON.stringify(payload()) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      setCount(d.count);
    } catch (e) { toast.error(e.message); } finally { setPreviewing(false); }
  };

  const sendTest = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Add a subject and body first'); return; }
    setTesting(true);
    try {
      const r = await adminFetch(`${API_URL}/api/admin/broadcast/test`, { method: 'POST', body: JSON.stringify({ ...payload(), test_email: testEmail }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success(`Test sent to ${d.sent_to}`);
    } catch (e) { toast.error(e.message); } finally { setTesting(false); }
  };

  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Add a subject and body first'); return; }
    const label = AUDIENCES.find((a) => a.id === audience)?.label || audience;
    let scheduledIso = null;
    if (scheduleOn) {
      if (!scheduledAt) { toast.error('Pick a date & time to schedule'); return; }
      const dt = new Date(scheduledAt);
      if (isNaN(dt.getTime()) || dt.getTime() <= Date.now()) { toast.error('Choose a future date & time'); return; }
      scheduledIso = dt.toISOString();
    }
    const when = scheduledIso ? `on ${new Date(scheduledIso).toLocaleString()}` : 'now';
    if (!window.confirm(`Send this broadcast to "${label}"${count != null ? ` (${count} recipients)` : ''} ${when}? This cannot be undone.`)) return;
    setSending(true);
    try {
      const r = await adminFetch(`${API_URL}/api/admin/broadcast/send`, { method: 'POST', body: JSON.stringify({ ...payload(), scheduled_at: scheduledIso }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      if (d.scheduled) {
        toast.success(`Scheduled to ${d.recipient_count} recipient(s) for ${new Date(d.scheduled_at).toLocaleString()}.`);
      } else {
        toast.success(`Broadcast queued to ${d.recipient_count} recipient(s). Delivering now.`);
      }
      setSubject(''); setBody(''); setHeaderImage(''); setHeaderLink(''); setDiscountCode(''); setUniqueCodes(false); setFeaturedIds([]); setCount(null); setScheduleOn(false); setScheduledAt('');
      setTimeout(loadHistory, 1500);
    } catch (e) { toast.error(e.message); } finally { setSending(false); }
  };

  const cancelScheduled = async (b) => {
    if (!window.confirm(`Cancel the scheduled broadcast "${b.subject}"?`)) return;
    try {
      const r = await adminFetch(`${API_URL}/api/admin/broadcast/${b.broadcast_id}/cancel`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success('Scheduled broadcast cancelled');
      loadHistory();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-6" data-testid="admin-broadcast-tab">
      <div>
        <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2"><Megaphone className="h-5 w-5 text-teal-700" /> Broadcast</h2>
        <p className="text-sm text-slate-500">Send an email to your customers or newsletter list. Every message includes a one-click unsubscribe — only opted-in recipients are targeted.</p>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-5">
          <div>
            <Label className="text-sm">Audience</Label>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-2">
              {AUDIENCES.map((a) => (
                <button
                  key={a.id}
                  onClick={() => setAudience(a.id)}
                  className={`text-left p-3 rounded-xl border transition-colors ${audience === a.id ? 'border-teal-600 bg-teal-50 ring-1 ring-teal-600' : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid={`broadcast-audience-${a.id}`}
                >
                  <div className="font-semibold text-slate-800 text-sm">{a.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{a.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {audience === 'custom' && (
            <div>
              <Label htmlFor="bc-custom" className="text-sm">Custom emails (comma or newline separated)</Label>
              <Textarea id="bc-custom" rows={3} value={customEmails} onChange={(e) => setCustomEmails(e.target.value)} placeholder="a@example.com, b@example.com" data-testid="broadcast-custom-emails" />
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-slate-600" data-testid="broadcast-count">
              <Users className="h-4 w-4" />
              {count == null ? 'Preview to see recipient count' : <span><strong className="text-slate-900">{count}</strong> recipient{count === 1 ? '' : 's'}</span>}
            </div>
            <Button variant="outline" size="sm" onClick={preview} disabled={previewing} data-testid="broadcast-preview-btn">
              {previewing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Preview count
            </Button>
          </div>

          <div>
            <Label htmlFor="bc-subject" className="text-sm">Subject</Label>
            <Input id="bc-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="A new peptide just landed" data-testid="broadcast-subject" />
          </div>

          <div>
            <Label htmlFor="bc-body" className="text-sm">Body (HTML supported — links, bold, lists)</Label>
            <Textarea id="bc-body" rows={10} value={body} onChange={(e) => setBody(e.target.value)} placeholder="<p>Hi there,</p><p>We just restocked your favorites...</p>" className="font-mono text-sm" data-testid="broadcast-body" />
            <p className="text-xs text-slate-400 mt-1">The email is auto-wrapped in the Genora template with an unsubscribe footer.</p>
          </div>

          <BroadcastHeaderImage adminFetch={adminFetch} imageUrl={headerImage} setImageUrl={setHeaderImage} link={headerLink} setLink={setHeaderLink} />
          <BroadcastFeaturedProducts adminFetch={adminFetch} ids={featuredIds} setIds={setFeaturedIds} />
          <BroadcastDiscountCode adminFetch={adminFetch} value={discountCode} setValue={pickCode} unique={uniqueCodes} setUnique={setUniqueCodes} />

          <div className="rounded-xl border border-slate-200 p-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={scheduleOn} onChange={(e) => setScheduleOn(e.target.checked)} className="h-4 w-4 accent-teal-600" data-testid="broadcast-schedule-toggle" />
              <span className="text-sm font-medium text-slate-700 inline-flex items-center gap-1"><CalendarClock className="h-4 w-4 text-teal-600" /> Schedule for later</span>
            </label>
            {scheduleOn && (
              <div className="mt-3">
                <Label htmlFor="bc-when" className="text-xs text-slate-500">Send date &amp; time (your local time)</Label>
                <Input id="bc-when" type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} className="w-full sm:w-72" data-testid="broadcast-schedule-datetime" />
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
            <Button variant="outline" onClick={showPreview} data-testid="broadcast-preview-email-btn"><Eye className="h-4 w-4 mr-1" /> Preview email</Button>
            <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="test@you.com" className="w-48 h-9" data-testid="broadcast-test-email" />
            <Button variant="outline" onClick={sendTest} disabled={testing} data-testid="broadcast-test-btn">
              {testing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FlaskConical className="h-4 w-4 mr-1" />} Test send
            </Button>
            <Button className="bg-teal-700 hover:bg-teal-800 text-white" onClick={send} disabled={sending} data-testid="broadcast-send-btn">
              {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : (scheduleOn ? <CalendarClock className="h-4 w-4 mr-1" /> : <Send className="h-4 w-4 mr-1" />)} {scheduleOn ? 'Schedule broadcast' : 'Send broadcast'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <BroadcastReportDialog adminFetch={adminFetch} broadcastId={reportId} onClose={() => setReportId(null)} />

      <Dialog open={previewHtml != null} onOpenChange={(o) => !o && setPreviewHtml(null)}>
        <DialogContent className="max-w-[680px] w-[95vw] p-4" onOpenAutoFocus={(e) => e.preventDefault()} data-testid="broadcast-preview-dialog">
          <DialogHeader><DialogTitle>Email preview</DialogTitle></DialogHeader>
          <iframe title="Email preview" tabIndex={-1} srcDoc={previewHtml || ''} className="w-full h-[70vh] border rounded-lg bg-white" data-testid="broadcast-preview-iframe" />
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base text-slate-800"><Sparkles className="h-5 w-5 text-teal-600" /> Recent broadcasts</CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-sm text-slate-400 py-3">Nothing sent yet — your first broadcast will appear here.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-4 font-medium">Subject</th>
                    <th className="py-2 pr-4 font-medium">Audience</th>
                    <th className="py-2 pr-4 font-medium">Sent</th>
                    <th className="py-2 pr-4 font-medium">When</th>
                    <th className="py-2 pr-4 font-medium">Status</th>
                    <th className="py-2 pr-4 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((b) => (
                    <tr key={b.broadcast_id} className="border-b border-slate-100" data-testid={`broadcast-row-${b.broadcast_id}`}>
                      <td className="py-2 pr-4 font-medium text-slate-800">{b.subject}</td>
                      <td className="py-2 pr-4 text-slate-600 capitalize">{b.audience}</td>
                      <td className="py-2 pr-4 text-slate-600">{b.sent_count}/{b.recipient_count}{b.failed_count ? ` (${b.failed_count} failed)` : ''}</td>
                      <td className="py-2 pr-4 text-slate-500">{b.status === 'scheduled' && b.scheduled_at ? fmtDate(b.scheduled_at) : fmtDate(b.created_at)}</td>
                      <td className="py-2 pr-4">
                        {b.status === 'sent' && <span className="inline-flex items-center gap-1 text-teal-700"><CheckCircle2 className="h-3.5 w-3.5" /> Sent</span>}
                        {b.status === 'sending' && <span className="inline-flex items-center gap-1 text-amber-600"><Clock className="h-3.5 w-3.5" /> Sending</span>}
                        {b.status === 'scheduled' && <span className="inline-flex items-center gap-1 text-blue-600"><CalendarClock className="h-3.5 w-3.5" /> Scheduled</span>}
                        {b.status === 'cancelled' && <span className="inline-flex items-center gap-1 text-slate-400"><XCircle className="h-3.5 w-3.5" /> Cancelled</span>}
                      </td>
                      <td className="py-2 pr-4 text-right whitespace-nowrap">
                        {(b.status === 'sent' || b.status === 'sending') && (
                          <Button size="sm" variant="ghost" className="text-teal-700 h-7" onClick={() => setReportId(b.broadcast_id)} data-testid={`broadcast-report-btn-${b.broadcast_id}`}>
                            <BarChart3 className="h-3.5 w-3.5 mr-1" /> Report
                          </Button>
                        )}
                        {b.status === 'scheduled' && (
                          <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 h-7" onClick={() => cancelScheduled(b)} data-testid={`broadcast-cancel-${b.broadcast_id}`}>
                            Cancel
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminBroadcastTab;
